import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import type { CheckoutCompletion, CheckoutOffer } from "../lib/types";
import { Button, ErrorNote, Field, Input, formatMoney } from "../components/ui";

/**
 * The one page in this app a stranger sees. No session, no navigation, no way
 * back into the rest of the system — a buyer opening a link from a marketer
 * should see what they are buying and nothing else.
 */
export function Pay() {
  const { token = "" } = useParams();

  const offer = useQuery({
    queryKey: ["checkout-offer", token],
    queryFn: () => api<CheckoutOffer>(`/api/v1/checkout/links/${token}`),
    retry: false,
  });

  if (offer.isPending) return <Frame>Loading…</Frame>;

  if (offer.isError) {
    const notFound = offer.error instanceof ApiError && offer.error.status === 404;
    return (
      <Frame>
        <h1 className="text-xl font-semibold tracking-tight">
          {notFound ? "This link is no longer valid" : "Something went wrong"}
        </h1>
        <p className="text-sm text-muted">
          {notFound
            ? "It may have expired. Ask whoever sent it to you for a new one."
            : "Reload the page to try again."}
        </p>
      </Frame>
    );
  }

  if (offer.data.status === "paid") {
    return (
      <Frame>
        <h1 className="text-xl font-semibold tracking-tight">Already paid</h1>
        <p className="text-sm text-muted">
          This purchase has gone through. Nothing further is needed, and you have not been charged
          again.
        </p>
      </Frame>
    );
  }

  return <Offer token={token} offer={offer.data} />;
}

function Offer({ token, offer }: { token: string; offer: CheckoutOffer }) {
  const [name, setName] = useState(offer.customerName ?? "");
  const [email, setEmail] = useState(offer.customerEmail ?? "");
  const [phone, setPhone] = useState(offer.customerPhone ?? "");

  const start = useMutation({
    mutationFn: () =>
      api<{ url: string; orderNumber: string }>(`/api/v1/checkout/links/${token}`, {
        method: "POST",
        body: { name: name.trim(), email: email.trim(), ...(phone.trim() === "" ? {} : { phone: phone.trim() }) },
      }),
    onSuccess: (result) => {
      // Leaves this app entirely. Card details are only ever typed on Stripe.
      window.location.assign(result.url);
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    start.mutate();
  }

  const recurring = offer.plan.interval !== "once" && (offer.plan.priceCents ?? 0) > 0;

  return (
    <Frame>
      <header className="space-y-1">
        <p className="field-label">ServiceFinder</p>
        {/* A product name is whatever the seller typed, so it has to be able
            to wrap on a phone rather than run off the side. */}
        <h1 className="break-words text-xl font-semibold tracking-tight sm:text-2xl">
          {offer.product.name}
        </h1>
      </header>

      <div className="space-y-3 rounded-lg border border-hairline bg-surface p-4">
        {offer.oneTimeCents > 0 && (
          <Line label="Purchase" value={formatMoney(offer.oneTimeCents, offer.currency)} />
        )}
        {recurring && (
          <Line
            label={`${offer.plan.name}, billed ${offer.plan.interval}ly`}
            value={`${formatMoney(offer.plan.priceCents, offer.currency)} / ${offer.plan.interval}`}
          />
        )}
        <div className="border-t border-hairline pt-3">
          <Line
            label="Due today"
            value={formatMoney(
              offer.oneTimeCents + (recurring ? (offer.plan.priceCents ?? 0) : 0),
              offer.currency,
            )}
            strong
          />
        </div>
        {recurring && (
          <p className="text-xs text-dim">
            The {offer.plan.interval}ly charge continues until you cancel. The purchase price is
            charged once.
          </p>
        )}
      </div>

      {offer.cardsAccepted ? (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Your name">
            <Input value={name} required onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field label="Email" hint="Where the receipt and licence key are sent.">
            <Input
              type="email"
              value={email}
              required
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          <Field label="Phone" hint="Optional.">
            <Input value={phone} onChange={(event) => setPhone(event.target.value)} />
          </Field>

          {start.isError && <ErrorNote>{(start.error as Error).message}</ErrorNote>}

          {/* Taller than a table button: this is the only control on the
              screen and it is pressed with a thumb. */}
          <Button
            type="submit"
            variant="primary"
            className="min-h-[2.75rem] w-full"
            disabled={start.isPending}
          >
            {start.isPending ? "Taking you to checkout…" : "Continue to payment"}
          </Button>
          <p className="text-center text-xs text-dim">
            Payment is handled by Stripe. Card details never reach us.
          </p>
        </form>
      ) : (
        // Honest rather than broken: the link is real, the sale is real, the
        // card form is not switched on in this deployment yet.
        <p className="rounded-md border border-pending/40 bg-pending/10 px-3 py-3 text-sm text-pending">
          Card payments are not available on this link yet. Reply to whoever sent it to you and they
          will arrange payment directly.
        </p>
      )}
    </Frame>
  );
}

/** Where Stripe returns a buyer who paid. */
export function PayComplete() {
  const [params] = useSearchParams();
  const session = params.get("session") ?? "";

  const completion = useQuery({
    queryKey: ["checkout-completion", session],
    queryFn: () => api<CheckoutCompletion>(`/api/v1/checkout/sessions/${session}`),
    enabled: session.length > 0,
    retry: false,
    // Stripe's confirmation arrives on its own connection, moments after the
    // buyer is redirected. Polling briefly is what turns "confirming" into a
    // licence without asking them to reload.
    refetchInterval: (query) => (query.state.data?.status === "paid" ? false : 2000),
  });

  if (session.length === 0) {
    return (
      <Frame>
        <h1 className="text-xl font-semibold tracking-tight">Nothing to show</h1>
        <p className="text-sm text-muted">This page is opened by Stripe after a payment.</p>
      </Frame>
    );
  }

  if (completion.isPending) return <Frame>Checking your payment…</Frame>;

  if (completion.isError) {
    return (
      <Frame>
        <h1 className="text-xl font-semibold tracking-tight">Payment received</h1>
        <p className="text-sm text-muted">
          We could not load the details just now, but your payment went through. Your receipt is on
          its way by email.
        </p>
      </Frame>
    );
  }

  const done = completion.data.status === "paid";

  return (
    <Frame>
      <header className="space-y-1">
        <p className="field-label">ServiceFinder</p>
        <h1 className="text-2xl font-semibold tracking-tight">
          {done ? "You're all set" : "Confirming your payment"}
        </h1>
      </header>

      <div className="space-y-3 rounded-lg border border-hairline bg-surface p-4">
        <Line label="Product" value={completion.data.productName} />
        <Line label="Order" value={completion.data.orderNumber} />
        {completion.data.licenseLast4 !== null && (
          <Line label="Licence ending" value={completion.data.licenseLast4} />
        )}
      </div>

      <p className="text-sm text-muted">
        {!done
          ? "This takes a few seconds. You can leave this page — your payment is safe either way."
          : completion.data.licenseEmailed
            ? "Your licence has been issued and the key is on its way to the email address you paid with."
            : // Only said when it is true. Promising an email that is not
              // coming is how someone sits waiting instead of asking us.
              "Your licence has been issued. We will be in touch shortly with your key."}
      </p>

      {/* Paying self-serve creates the account, so there is one to sign into by
          the time anybody reads this. Without a way through, the next question
          — where do I see what I just bought — has no answer on the page that
          raised it. */}
      {done && (
        <div className="space-y-2 border-t border-hairline pt-4">
          <Link
            to="/"
            className="inline-block rounded-md bg-accent px-3 py-2 text-sm font-semibold text-base"
          >
            Sign in to your account
          </Link>
          <p className="text-xs text-dim">
            Your licences, orders and projects live there, under the email address you paid with.
          </p>
        </div>
      )}
    </Frame>
  );
}

/** Where Stripe returns a buyer who backed out. */
export function PayCancelled() {
  return (
    <Frame>
      <h1 className="text-xl font-semibold tracking-tight">Payment cancelled</h1>
      <p className="text-sm text-muted">
        You have not been charged. Open your link again whenever you are ready.
      </p>
      <Link to="/" className="inline-block py-2 text-sm text-accent underline underline-offset-4">
        Back to sign in
      </Link>
    </Frame>
  );
}

function Frame({ children }: { children: ReactNode }) {
  return (
    // Gutters shrink rather than disappear on a narrow phone: a card flush
    // against the bezel reads as a rendering fault, not as a design.
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 px-4 py-12 sm:px-6 sm:py-16">
      {children}
    </main>
  );
}

function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    // When the two halves cannot sit side by side the value takes a line of its
    // own rather than being squeezed — an amount broken mid-figure is the one
    // thing on this page that must never be ambiguous.
    <div className="flex flex-wrap items-baseline justify-between gap-x-4">
      <span className={strong ? "text-sm font-medium text-ink" : "text-sm text-muted"}>{label}</span>
      <span
        className={
          strong
            ? "tabular ml-auto break-words text-right font-semibold text-ink"
            : "tabular ml-auto break-words text-right text-ink"
        }
      >
        {value}
      </span>
    </div>
  );
}
